import React from "react";
import themePropTypes from "../theme-prop-types";
import renderFields from "../../screens/render-fields";
import Toasts from "../../components/toast-notification";
import ErrorBox from "../../components/error-box";
import env from "../../utils/env";
import { buildTrackingEvent, getEvent } from "../../utils/tracking-events";
import {
  SCREENS_WITHOUT_BACK_BUTTON,
  DIALOG_NAMES,
  CONFIRMATION_SCREENS,
  SCREEN_SIZE_LANDING_BACK_BUTTON,
  UNIFIED_PROFILE_PARAMS
} from "../../utils/constants";
import FixedLayoutFieldsRenderer from "./fixed-layout-fields-renderer";
import { setFieldsErrorsFromBack } from "../../utils/field-validation";
import { MessagingContext } from "../../context/messaging-layer";

const VALIDATION_TOAST_ID = "__validation__";

/**
 * This is the default screen component. It contains the default layout for the screen including the title,
 * back button, and the content. Calls renderFields to generate all of the included field and footerField components
 * in the screen.
 * Updates the screenHistory with the current screen name. Also starts processing the Piwik tracking events.
 * @class Screen
 * @property {object} props passed from [BaseViewController]{@link BaseViewController}
 * @property {object} props.screen Contains screen content and settings to be rendered
 * @property {object} props.appData Contains data on flow and screen, as well as methods (i.e. screen data, history,
 * validators)
 * @property {object} props.components All default theme components from [Default Themes]{@link module:theme/default}
 * @property {bool} props.isRoot
 * @returns {React.Component}
 */
class Screen extends React.Component {
  constructor(props) {
    super(props);
    // eslint-disable-next-line no-param-reassign
    props.appData.screenValidation.setScreenValidity =
      this.setScreenValidity.bind(this);
    this.viewRef = React.createRef();
    this.screenErrorRef = React.createRef();
    this.state = { LandingPageBackButton: false };
  }

  resizeLandingButton = () => {
    let width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;

    if (width < SCREEN_SIZE_LANDING_BACK_BUTTON) {
      this.setState({ LandingPageBackButton: true });
    } else {
      this.setState({ LandingPageBackButton: false });
    }
  };

  componentDidMount() {
    this.processTrackingEvent(); // Piwik

    const {
      screen,
      appData: {
        isPulse,
        isEnhancedRedesign,
        isUnifiedRegistration,
        resetFieldValidationAll,
        screensHistoryFlow,
        updateScreensHistoryFlow,
        screenIndex,
        settings,
        properLink: isDfd
      }
    } = this.props;

    const isFixedLayoutFieldsRender = screen.screenLayouts?.length;

    if (!isFixedLayoutFieldsRender) {
      this.viewRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }

    const screensWithoutChangingHash = [
      "ConsultationPhotoModal",
      "DermSearchTreatment",
      "DermAddTreatment"
    ];

    if (isPulse || isEnhancedRedesign || isUnifiedRegistration) {
      resetFieldValidationAll();
    }

    if (settings?.translations?.wcag_title && screen.title) {
      document.title = `${settings?.translations?.wcag_title}-${screen.title}`;
    }

    if (screen?.params?.includes(UNIFIED_PROFILE_PARAMS.landingPage)) {
      window.addEventListener("resize", this.resizeLandingButton);
    }

    if (env("BROWSER_NAVIGATION_ENABLED")) {
      if (
        screensHistoryFlow.indexOf(screen.name) === -1 &&
        screen.type !== "modal_screen"
      ) {
        updateScreensHistoryFlow({
          name: this.props.screen.name,
          index: screenIndex
        });
      }

      // Don't change hash when we go to screen from screensWithoutChangingHash[]
      if (
        !screensWithoutChangingHash.includes(screen.name) &&
        !(screen.params || "").includes("TDScreenOptionUnchangebleHash") &&
        !isDfd &&
        screen.type !== "modal_screen"
      ) {
        const baseUrl = window.location.href.split("#")[0];
        if (screen?.params?.includes("TDScreenOptionStartNewFlow")) {
          window.history.replaceState(null, "", `${baseUrl}#${screen.name}`);
        } else {
          window.location.assign(`${baseUrl}#${screen.name}`);
        }
      }
    }
  }

  UNSAFE_componentWillReceiveProps({
    appData: {
      validation: { valid: nextValid },
      removeToast
    }
  }) {
    const {
      appData: {
        validation: { valid: prevValid }
      }
    } = this.props;
    if (!prevValid && nextValid) {
      removeToast(VALIDATION_TOAST_ID);
    }
  }

  componentDidUpdate() {
    if (this.shouldRenderFormCommonError()) {
      this.props.appData.screenRef = this.screenErrorRef;
    }

    if (this.props?.appData?.isPulse) {
      this.props.appData.screenRef = this.viewRef;
    }

    if (this.hasBackendErrors()) {
      setFieldsErrorsFromBack(this.props.appData);
    }

    // eslint-disable-next-line react/destructuring-assignment
    this.props.appData.screenValidation.setScreenValidity =
      this.setScreenValidity.bind(this);
    const { screen, appData, components } = this.props;

    if (this.shouldShowDefaultDialog()) {
      const mainDialog = screen.dialogs.find(
        dialog => dialog.name === DIALOG_NAMES.screenDefault
      );

      const onClose = dialog => {
        dialog.isClosed = true;
        if (this.context?.hideDialog) {
          this.context.hideDialog();
        }
      };

      const withClose = () =>
        typeof mainDialog.data?.showDismiss === "boolean"
          ? mainDialog.data?.showDismiss
          : true;

      if (!mainDialog.isClosed) {
        mainDialog.isClosed = this.context.showDialog({
          content: {
            dialogFields: true,
            dialogOverlay: false,
            dismissTime: mainDialog.data?.autoDismissDuration,
            dialogClass: `${mainDialog.name} ${
              mainDialog.data?.classNameBody || ""
            } ${mainDialog.data?.type || ""}`,
            i18nDismissLabel: mainDialog.data?.i18nDismissLabel,
            children: renderFields(
              mainDialog.fields,
              appData,
              components,
              screen
            ),
            withClose: withClose(),
            onClose: () => onClose(mainDialog)
          }
        });
      }
    }
  }

  componentWillUnmount() {
    if (
      this.shouldShowDefaultDialog() &&
      !this.props.screen.isClosed &&
      this.context?.hideDialog
    ) {
      this.context.hideDialog();
    }

    if (
      this.props.screen.params
        ?.split("|")
        .includes(UNIFIED_PROFILE_PARAMS.landingPage)
    ) {
      window.removeEventListener("resize", this.resizeLandingButton);
    }
  }

  setScreenValidity() {
    const {
      appData: {
        screenValidation: { validationMessage },
        addToast
      }
    } = this.props;
    addToast({ id: VALIDATION_TOAST_ID, message: validationMessage });
  }

  handleBackButtonWrapper = () => {
    const { appData } = this.props;

    appData.handleBackButtonClick();
    appData.removeToast("__validation__");
  };

  hasBackendErrors = () => {
    const {
      appData: {
        data: { errors },
        isUnifiedRegistration
      }
    } = this.props;

    return isUnifiedRegistration && errors && errors.length;
  };

  shouldRenderBackButton = () => {
    const { appData, screen, isRoot } = this.props;
    const isPrevScreenFromHistory = !screen.hiddenFields?.return_back_url;

    if (!env("SHOW_BACK_BUTTON")) return false;
    if (screen.params?.includes(UNIFIED_PROFILE_PARAMS.landingPage))
      return true;
    if (screen.params?.includes("TDScreenHasGoHomeButton")) return true;
    return (
      isRoot &&
      screen.type !== "modal_screen" &&
      !screen.hideBackButton &&
      !CONFIRMATION_SCREENS.includes(screen.name) &&
      appData.history &&
      (appData.history.length > 1 ||
        (appData.history.length === 1 && !isPrevScreenFromHistory))
    );
  };

  shouldRenderLandingBackButton = () => {
    if (
      this.props.screen.params
        ?.split("|")
        .includes(UNIFIED_PROFILE_PARAMS.landingPage) &&
      window.innerWidth > SCREEN_SIZE_LANDING_BACK_BUTTON
    ) {
      return false;
    }
    return true;
  };

  shouldRenderFormCommonError = () => {
    const {
      appData: {
        isUnifiedRegistration,
        data: { error }
      }
    } = this.props;

    return isUnifiedRegistration && error;
  };

  shouldShowDefaultDialog() {
    return (
      this.props.screen.params
        ?.split("|")
        .includes("TDScreenOptionShowDefaultDialog") &&
      !this.props.screen.params?.split("|").includes("TDScreenOptionIsHome") &&
      this.props.screen.dialogs.find(
        dialog => dialog.name === DIALOG_NAMES.screenDefault
      )
    );
  }

  processTrackingEvent() {
    const { name: screenName } = this.props.screen;
    const {
      values: { logged_in_member_id: userId }
    } = this.props.appData;
    const bundleIdentifier = env("BUNDLE_ID");
    const trackingEvent = buildTrackingEvent({
      bundleIdentifier,
      userId,
      screenName
    });

    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(trackingEvent);
    } else {
      window.dataLayer = [];
    }
    const { data } = this.props.screen;
    if (data?.screenTracking) {
      window.dataLayer.push(getEvent(data.screenTracking));
    }
  }

  returningFromShoppingCart() {
    const appData = { ...this.props.appData };
    if (appData.history) {
      const uRegConfirmCoverageScreen = appData.history[0];
      const shoppingCartScreen = appData.history[1];

      if (uRegConfirmCoverageScreen && shoppingCartScreen) {
        const val = !!(
          shoppingCartScreen.name === "ShoppingCartScreenSetting" &&
          uRegConfirmCoverageScreen.name === "SelectCoverage"
        );
        return val;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  backButtonShouldBeHidden(screen, appData) {
    const previousScreen = appData.history[1];
    return (
      (SCREENS_WITHOUT_BACK_BUTTON.includes(screen.name) ||
        (screen.params || "").includes("TDScreenRemoveBackButton") ||
        (screen.params || "").includes("TDScreenOptionHideBackButton")) &&
      previousScreen?.name !== "InboxScreen"
    );
  }

  render() {
    const { screen, appData, components } = this.props;
    const { fields = [], footerFields = [] } = screen;

    const val = this.returningFromShoppingCart();
    if (this.backButtonShouldBeHidden(screen, appData)) {
      if (val) {
        screen.hideBackButton = false;
      } else {
        screen.hideBackButton = true;
      }
    }
    const containsSecondary = [...fields, ...footerFields].some(f =>
      (f.params || "").includes("TDFieldOptionSecondaryAction")
    );

    const isModal = screen.type === "modal_screen";
    const isProgressBarVisible = screen.progress > 0;
    const inertProps = {};

    if (appData.dialogInitialized && !isModal) {
      inertProps.inert = true;
    }

    const isFixedLayoutFieldsRender = screen.screenLayouts?.length;

    if (
      (screen.identifier === "BloodPressureMeasurementForm" ||
        screen.identifier === "HeartRateMeasurementForm") &&
      appData.toasts.length &&
      appData.toasts[0].message.startsWith("Variable")
    ) {
      appData.toasts = [
        {
          ...appData.toasts,
          message:
            appData.settings.translations.measurement_input_format_validation
        }
      ];
    }

    return isFixedLayoutFieldsRender ? (
      <FixedLayoutFieldsRenderer
        screen={screen}
        appData={appData}
        components={components}
      />
    ) : (
      <div
        ref={this.viewRef}
        data-testid={`screen-${screen.name}`}
        className={`screen-web-sdk ${screen.name}`}
        {...inertProps}
      >
        <Toasts
          toasts={appData.toasts}
          onClose={toastId => appData.removeToast(toastId)}
          components={components}
          settings={appData.settings}
        />
        {/* Currently, WebSDK supports browser navigation. Therefore, WebSDK's back button is absent by default.
          If you want to see and use WebSDK's back button, add specific property to appConfiguration. */}
        {this.shouldRenderBackButton() &&
          this.shouldRenderLandingBackButton() && (
            <button
              data-testid='button-back'
              type='button'
              className='button-left-arrow-web-sdk'
              onClick={this.handleBackButtonWrapper}
              aria-label={
                screen.params?.includes("TDScreenHasGoHomeButton")
                  ? appData.settings?.translations?.return_home
                  : appData.settings?.translations?.return_previous_page
              }
            >
              {appData.settings.translations.back}
            </button>
          )}
        {isProgressBarVisible && (
          <components.ProgressBar
            value={screen.progress}
            progressLabel={screen.progressLabel}
            progressStep={screen.progressStep}
            progressAriaLabel={screen.progressAriaLabel}
          />
        )}
        {screen?.title?.length ? (
          <components.Title
            title={screen.title}
            forceFocus={!isModal && !isProgressBarVisible}
          />
        ) : null}
        {screen?.description?.length ? (
          <components.Description
            description={screen.description}
            forceFocus={!isModal && !isProgressBarVisible}
          />
        ) : null}
        {!this.props?.appData?.isPulse &&
          this.shouldRenderFormCommonError() && (
            <ErrorBox
              ref={this.screenErrorRef}
              errorMessage={appData.data?.error}
            />
          )}
        <div
          className={`screen-fields-web-sdk ${
            containsSecondary ? "contains-secondary" : ""
          }`}
        >
          {renderFields(fields, appData, components, screen)}
          {renderFields(footerFields, appData, components, screen)}
        </div>
      </div>
    );
  }
}

Screen.propTypes = themePropTypes.screen;
Screen.contextType = MessagingContext;

export default Screen;
